import { Fragment, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useComponentOptionsQuery, useLogsQuery, usePurgeLogs } from "../hooks/useLogs";
import GoHomeButton from "./GoHomeButton";

const SEVERITY_OPTIONS = ["All", "Info", "Warn", "Error"];

const SEVERITY_COLORS = {
  info:  { bg: "#e3f0fb", text: "#1565c0", border: "#1565c0" },
  warn:  { bg: "#fff3e0", text: "#e65100", border: "#e65100" },
  error: { bg: "#fdecea", text: "#c62828", border: "#c62828" },
};

function severityStyle(level) {
  const key = (level || "").toLowerCase();
  return SEVERITY_COLORS[key] || null;
}

function SeverityChip({ label, size = "small" }) {
  const style = severityStyle(label);
  if (!style) return <Chip label={label} size={size} />;
  return (
    <Chip
      label={label}
      size={size}
      sx={{
        bgcolor: style.bg,
        color: style.text,
        border: `1px solid ${style.border}`,
        fontWeight: 600,
      }}
    />
  );
}

export default function AdminLogs({ onNavigateHome }) {
  // Filters
  const [severityFilter, setSeverityFilter] = useState("All");
  const [componentFilter, setComponentFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchText, setSearchText] = useState("");
  // Typing shouldn't fire a query per keystroke.
  const [appliedSearch, setAppliedSearch] = useState("");

  // Expanded details rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Purge dialog
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeStatus, setPurgeStatus] = useState(null); // { type, msg }

  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  // Filtering happens in Postgres, not in the browser. It used to pull the newest
  // 50 rows and filter those, which meant a search for anything older silently
  // reported "No logs found" while the row was sitting in the table.
  const {
    data,
    isLoading: loading,
    isFetchingNextPage: loadingMore,
    hasNextPage: hasMore,
    fetchNextPage,
    error,
  } = useLogsQuery({
    severity: severityFilter,
    component: componentFilter,
    dateFrom,
    dateTo,
    search: appliedSearch,
  });

  const logs = data?.pages.flatMap((page) => page.rows) ?? [];
  const errorMessage = error?.message ?? null;
  const loadMore = () => fetchNextPage();

  // ── Component dropdown options ─────────────────────────────────────────────
  const componentOptionsQuery = useComponentOptionsQuery();
  const componentOptions = ["All", ...(componentOptionsQuery.data ?? [])];

  // ── Toggle expanded row ────────────────────────────────────────────────────
  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // ── Purge ──────────────────────────────────────────────────────────────────
  const purgeMutation = usePurgeLogs();

  const handlePurge = () => {
    purgeMutation.mutate(undefined, {
      onSuccess: () => {
        setPurgeStatus({ type: "success", msg: "Log entries older than 90 days were deleted." });
        setPurgeOpen(false);
      },
      onError: (err) => {
        setPurgeStatus({ type: "error", msg: `Purge failed: ${err.message}` });
        setPurgeOpen(false);
      },
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Paper elevation={2} className="page-card" sx={{ p: { xs: 1.5, sm: 3 }, mt: 3, mb: 4 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" className="section-header">
          Admin – Logs
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <GoHomeButton onNavigateHome={onNavigateHome} />
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => setPurgeOpen(true)}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Purge logs older than 90 days
          </Button>
        </Stack>
      </Stack>

      {purgeStatus && (
        <Alert severity={purgeStatus.type} sx={{ mb: 2 }} onClose={() => setPurgeStatus(null)}>
          {purgeStatus.msg}
        </Alert>
      )}

      {/* ── Filters ── */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        {/* Severity chips */}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {SEVERITY_OPTIONS.map((opt) => {
            const isSelected = severityFilter === opt;
            const style = opt === "All" ? null : severityStyle(opt);
            return (
              <Chip
                key={opt}
                label={opt}
                onClick={() => setSeverityFilter(opt)}
                variant={isSelected ? "filled" : "outlined"}
                sx={
                  isSelected && style
                    ? { bgcolor: style.bg, color: style.text, border: `1px solid ${style.border}`, fontWeight: 700, cursor: "pointer" }
                    : isSelected
                    ? { fontWeight: 700, cursor: "pointer" }
                    : style
                    ? { color: style.text, border: `1px solid ${style.border}`, cursor: "pointer" }
                    : { cursor: "pointer" }
                }
              />
            );
          })}
        </Stack>

        {/* Component + date range + search */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "flex-end" }}>
          <FormControl size="small" sx={{ minWidth: 180, width: { xs: "100%", sm: 180 } }}>
            <InputLabel>Component</InputLabel>
            <Select
              label="Component"
              value={componentFilter}
              onChange={(e) => setComponentFilter(e.target.value)}
            >
              {componentOptions.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="From"
            size="small"
            slotProps={{ htmlInput: { type: "date" }, inputLabel: { shrink: true } }}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          />

          <TextField
            label="To"
            size="small"
            slotProps={{ htmlInput: { type: "date" }, inputLabel: { shrink: true } }}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          />

          <TextField
            label="Search action / component / details"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ flex: 1, minWidth: 200, width: { xs: "100%", sm: "auto" } }}
          />

          <Button
            size="small"
            variant="text"
            onClick={() => {
              setSeverityFilter("All");
              setComponentFilter("All");
              setDateFrom("");
              setDateTo("");
              setSearchText("");
            }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Clear
          </Button>
        </Stack>
      </Stack>

      {/* ── Table ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : errorMessage ? (
        <Typography color="error" sx={{ py: 4, textAlign: "center" }}>
          {errorMessage}
        </Typography>
      ) : logs.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No logs found.
        </Typography>
      ) : (
        <TableContainer component={Box} sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                <TableCell>Timestamp</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Component</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((row) => {
                const isExpanded = expandedRows.has(row.id);
                const hasDetails = row.details != null;
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      sx={{ verticalAlign: "top", "&:hover": { bgcolor: "rgba(0,0,0,0.03)" } }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                        {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>
                        <SeverityChip label={row.severity || "—"} />
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>
                        {row.user_id || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>
                        {row.component || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.82rem" }}>
                        {row.action || "—"}
                      </TableCell>
                      <TableCell>
                        {hasDetails ? (
                          <IconButton
                            size="small"
                            aria-label={isExpanded ? "Collapse details" : "Expand details"}
                            onClick={() => toggleRow(row.id)}
                            sx={{ fontSize: "0.75rem", p: 0.5 }}
                          >
                            {isExpanded ? "▼" : "▶"}
                          </IconButton>
                        ) : (
                          <Typography component="span" sx={{ color: "text.disabled", fontSize: "0.8rem" }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ pt: 0, pb: 1, bgcolor: "rgba(0,0,0,0.02)" }}>
                          <Box
                            component="pre"
                            sx={{
                              m: 0,
                              p: 1.5,
                              fontSize: "0.75rem",
                              fontFamily: "monospace",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all",
                              bgcolor: "rgba(0,0,0,0.04)",
                              borderRadius: 1,
                              maxHeight: 320,
                              overflowY: "auto",
                            }}
                          >
                            {JSON.stringify(row.details, null, 2)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !errorMessage && (
        <Stack alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Showing {logs.length} entr{logs.length === 1 ? "y" : "ies"}
            {hasMore ? " so far" : ""}
          </Typography>
          {hasMore && (
            <Button size="small" variant="outlined" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? "Loading..." : "Load more"}
            </Button>
          )}
        </Stack>
      )}

      {/* ── Purge confirmation dialog ── */}
      <Dialog open={purgeOpen} onClose={() => setPurgeOpen(false)}>
        <DialogTitle>Purge Old Logs</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all log entries older than 90 days. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handlePurge}>
            Purge
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
